import { ConfigService } from '@maia/config';
import { format, isDate } from 'date-fns';
import {
  BadRequestException,
  BaseExceptionContext,
  ForbiddenException,
  NotFoundException,
  ServerErrorException,
  UnexpectedException,
} from '@maia/exceptions';

import { IPaginationEntity } from '@maia/pagination';
import axios, { AxiosInstance, AxiosRequestConfig, isAxiosError } from 'axios';

import Cookies from 'js-cookie';
import { redirect } from '@tanstack/react-router';

export interface BaseResponse<T> {
  response: T;
  error: string | null;
  success: boolean;
  pagination: IPaginationEntity;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      startTime: Date;
      endTime: Date;
      durationMs?: number;
      durationSec?: number;
    };
  }
}

export class AxiosService {
  private static instance: AxiosInstance;
  private static isRefreshing = false; // Flag to track if refresh is in progress
  private static refreshSubscribers: Array<(token: string) => void> = []; // Queue for pending requests

  public static getInstance(): AxiosInstance {
    if (!AxiosService.instance) {
      this.instance = axios.create({
        adapter: 'fetch',
      });

      this.instance.interceptors.request.use((config) => {
        (config as any).metadata = { startTime: new Date() };

        const cookies = Cookies.get('auth_tokens');
        if (cookies) {
          const { accessToken } = JSON.parse(cookies);
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      });

      this.instance.interceptors.response.use(
        (response) => {
          const metadata = (response.config as any)?.metadata;
          if (metadata?.startTime) {
            const endTime = new Date();
            metadata.duration =
              endTime.getTime() - metadata.startTime.getTime();
          }
          return response;
        },
        async (error) => {
          const config = error?.config as AxiosRequestConfig;

          if (config?.metadata?.startTime) {
            const endTime = new Date();
            config.metadata.endTime = endTime;
            const durationMs =
              endTime.getTime() - config?.metadata?.startTime?.getTime();
            config.metadata.durationMs = durationMs;
            config.metadata.durationSec = durationMs / 1000;
          }

          if (isAxiosError(error) && error.response?.status === 401) {
            const cookies = Cookies.get('auth_tokens');
            if (!cookies) {
              Cookies.remove('auth_tokens');
              throw redirect({ to: '/' });
            }

            const { refreshToken } = JSON.parse(cookies);

            if (!AxiosService.isRefreshing) {
              AxiosService.isRefreshing = true;

              try {
                const res = await fetch(
                  `${import.meta.env.VITE_MAIA_API_PATH}/api/auth/refresh`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }),
                  },
                );

                if (!res.ok) {
                  Cookies.remove('auth_tokens');
                  throw redirect({ to: '/' });
                }

                const { tokenRes } = await res.json();

                Cookies.set('auth_tokens', JSON.stringify(tokenRes), {
                  secure: true,
                  sameSite: 'strict',
                  expires: new Date(tokenRes.refreshTokenExpiresAt),
                });

                AxiosService.isRefreshing = false;

                // Notify all subscribers with the new token
                AxiosService.refreshSubscribers.forEach((callback) =>
                  callback(tokenRes.accessToken),
                );
                AxiosService.refreshSubscribers = [];

                return this.instance.request({
                  ...config,
                  metadata: config?.metadata,
                  headers: {
                    ...config?.headers,
                    Authorization: `Bearer ${tokenRes.accessToken}`,
                  },
                });
              } catch (refreshError) {
                AxiosService.isRefreshing = false;
                AxiosService.refreshSubscribers = [];
                throw refreshError;
              }
            }

            // Queue the request until refresh finishes
            return new Promise((resolve) => {
              AxiosService.refreshSubscribers.push((newToken: string) => {
                const newRequest = {
                  ...config,
                  metadata: config?.metadata,
                  headers: {
                    ...config?.headers,
                    Authorization: `Bearer ${newToken}`,
                  },
                };
                resolve(this.instance.request(newRequest));
              });
            });
          }

          return Promise.reject(error);
        },
      );
    }
    return AxiosService.instance;
  }
}

export class HttpService {
  private readonly instance: AxiosInstance = AxiosService.getInstance();
  private configService: ConfigService;
  private endpoint: keyof ConfigService['endpoints'];

  constructor(
    configService: ConfigService,
    endpoint: keyof ConfigService['endpoints'] = 'baseUrl',
  ) {
    this.configService = configService;
    this.endpoint = endpoint;
  }

  private handleError(
    error: unknown,
    entityName: string,
    query: string,
  ): never {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;

      const baseContext: BaseExceptionContext = {
        service: 'api',
        entity: entityName,
        request: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          //   query:
          headers:
            typeof error.config?.headers.toJSON === 'function'
              ? (error.config?.headers.toJSON(true) as Record<string, string>)
              : undefined,
          body: error.config?.data,
          startTime: error?.config?.metadata?.startTime?.toISOString(),
        },
        response: {
          headers:
            typeof error.response?.headers.toJSON === 'function'
              ? (error.response?.headers.toJSON(true) as Record<string, string>)
              : undefined,
          response: error.response?.data,
          endTime: error?.config?.metadata?.endTime?.toISOString(),
          durationMs: error?.config?.metadata?.durationMs,
          durationSec: error?.config?.metadata?.durationSec,
          statusCode: error.response?.status,
        },
      };

      let errorToBeThrown: Error = new UnexpectedException(baseContext);

      switch (statusCode) {
        case 400:
          errorToBeThrown = new BadRequestException(entityName, baseContext);
          break;
        case 403:
          errorToBeThrown = new ForbiddenException(entityName, baseContext);
          break;
        case 404:
          errorToBeThrown = new NotFoundException(
            entityName,
            query,
            baseContext,
          );
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorToBeThrown = new ServerErrorException(entityName, baseContext);
          break;
      }

      throw errorToBeThrown;
    }

    throw error;
  }

  private handleResponse<T>(
    data: BaseResponse<T>,
    status: number,
    entityName: string,
    query: string,
  ): Pick<BaseResponse<T>, 'response' | 'pagination'> {
    if (status === 200 && data.response === null) {
      throw new NotFoundException(entityName, query, {
        service: 'api',
        response: {
          statusCode: status,
        },
        reason: `${entityName} data is null`,
      });
    }

    return {
      response: data.response,
      pagination: data.pagination,
    };
  }

  public async fetch<T>(
    query: string,
    entityName: string,
  ): Promise<Pick<BaseResponse<T>, 'response' | 'pagination'>> {
    try {
      const { data, status } = await this.instance.get<BaseResponse<T>>(
        `${this.configService.get(this.endpoint)}/${query}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return this.handleResponse(data, status, entityName, query);
    } catch (error: unknown) {
      this.handleError(error, entityName, query);
    }
  }

  public async post<TResponse, TPayload>(
    query: string,
    payload: TPayload,
    entityName: string,
  ): Promise<Pick<BaseResponse<TResponse>, 'response' | 'pagination'>> {
    try {
      const { data, status } = await this.instance.post<
        BaseResponse<TResponse>
      >(`${this.configService.get(this.endpoint)}/${query}`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse(data, status, entityName, query);
    } catch (error: unknown) {
      this.handleError(error, entityName, query);
    }
  }

  public async put<TResponse, TPayload>(
    query: string,
    payload: TPayload,
    entityName: string,
  ): Promise<Pick<BaseResponse<TResponse>, 'response' | 'pagination'>> {
    try {
      const { data, status } = await this.instance.put<BaseResponse<TResponse>>(
        `${this.configService.get(this.endpoint)}/${query}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return this.handleResponse(data, status, entityName, query);
    } catch (error: unknown) {
      this.handleError(error, entityName, query);
    }
  }

  public async delete<TResponse>(
    query: string,
    entityName: string,
  ): Promise<Pick<BaseResponse<TResponse>, 'response' | 'pagination'>> {
    try {
      const { data, status } = await this.instance.delete<
        BaseResponse<TResponse>
      >(`/${query}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return this.handleResponse(data, status, entityName, query);
    } catch (error: unknown) {
      this.handleError(error, entityName, query);
    }
  }
}
